import { Component, Inject } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApproachProfileService } from 'src/app/core/services/approach-profile.service';
import { UtilService } from 'src/app/core/services/util.service';

@Component({
  selector: 'app-approach-profile',
  templateUrl: './approach-profile.component.html',
  styleUrls: ['./approach-profile.component.scss']
})
export class ApproachProfileComponent {

  action: 'add' | 'edit' = 'add';
  rowData: any = null;

  userForm: FormGroup = new FormGroup({});
  formFields = [
    { name: 'ApproachName', label: 'Approach Name', type: 'text', space: 1, validators: [Validators.required]},
    { name: 'ApproachKey', label: 'Approach Key', type: 'text', space: 1, validators: [Validators.required]},
    { name: 'DisplayName', label: 'Display Name', type: 'text', space: 1, validators: []},
    { name: 'RenewalYear', label: 'Renewal Year', type: 'select', space: 1, validators: [Validators.required], options: this.profileService.renewalYearOptions()},
    { name: 'DisplayOrder', label: 'Display Order', type: 'text', space: 1, validators: [Validators.required]},
    { name: 'OpenDate', label: 'Open Date', type: 'date', space: 1, validators: [Validators.required]},
    { name: 'CloseDate', label: 'Close Date', type: 'date', space: 1, validators: [Validators.required]},
    { name: 'IsAvailable', label: 'Available ', type: 'checkbox', space: 1, validators: []},
    { name: 'IsGeneric', label: 'Generic Approach', type: 'checkbox', space: 1, validators: []},
    { name: 'FormCode', label: 'Form Code (defaults to approach name)', type: 'text', space: 1, validators: []},
    { name: 'Fee', label: 'Fee (blank uses the wizard default)', type: 'text', space: 1, validators: []},
    { name: 'DeadlineType', label: 'Deadline Type', type: 'select', space: 1, validators: [], options: [
      { label: 'None', value: '' },
      { label: 'Fixed (Close Date)', value: 'fixed' },
      { label: 'Rolling (Days from Activity)', value: 'rolling' },
    ]},
    { name: 'DeadlineDaysStarted', label: 'Deadline Days (Started)', type: 'text', space: 1, validators: []},
    { name: 'DeadlineDaysSubmitted', label: 'Deadline Days (Submitted)', type: 'text', space: 1, validators: []},
    { name: 'DeadlineDaysMoreInfo', label: 'Deadline Days (More Info)', type: 'text', space: 1, validators: []},
  ];
  submitting = false;

  constructor(
    public dialogRef: MatDialogRef<ApproachProfileComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public profileService: ApproachProfileService,
    private utilService: UtilService
  ) {
    this.action = data?.action || 'add';
    this.rowData = data?.data || null;
  }

  ngAfterViewInit() {
    if (this.action === 'edit' && this.rowData) {
      this.rowData.OpenDate = new Date(this.rowData.OpenDate);
      this.rowData.CloseDate = new Date(this.rowData.CloseDate);

      // The select's option values are strings but the API returns a number, and mat-select
      // matches with ===, so an un-coerced year leaves the control blank.
      this.rowData.RenewalYear = this.rowData.RenewalYear?.toString();

      setTimeout(() => {
        this.userForm.patchValue(this.rowData);

        // RenewalYear and ApproachName are the table's hash and range key. The update handler
        // looks the row up by that pair, so editing either does not rename the profile - it
        // 404s. Lock them once the row exists; submit() reads raw values so they still post.
        this.userForm.get('RenewalYear')?.disable();
        this.userForm.get('ApproachName')?.disable();
      });
    }
  }

  async submit() {
    if (!this.userForm.valid) return;

    const val = this.userForm.getRawValue();
    const payload: any = {
      ...val,
      DisplayOrder: Number(val.DisplayOrder),
      RenewalYear: Number(val.RenewalYear),
    };

    // Checkboxes: an untouched control is null, which would persist as null rather than false.
    payload.IsAvailable = !!val.IsAvailable;
    payload.IsGeneric = !!val.IsGeneric;

    // Fee is nullable on purpose - blank means "use the wizard template's fee" and 0 means a
    // genuinely free approach, so this cannot use the truthiness test the other fields use.
    // A typed "0" survives that test only by accident of being a non-empty string.
    if (val.Fee !== null && val.Fee !== undefined && String(val.Fee).trim() !== '') {
      payload.Fee = Number(val.Fee);
    } else {
      delete payload.Fee;
    }

    // Optional free text: omit rather than persisting an empty string, so the C# side sees null
    // and the "?? fallback" resolutions behave as designed.
    ['DisplayName', 'FormCode'].forEach((f) => {
      if (payload[f] === null || payload[f] === undefined || String(payload[f]).trim() === '') {
        delete payload[f];
      } else {
        payload[f] = String(payload[f]).trim();
      }
    });

    // Convert deadline days to numbers, strip empty values
    if (val.DeadlineType) {
      payload.DeadlineType = val.DeadlineType;
    } else {
      delete payload.DeadlineType;
    }
    if (val.DeadlineDaysStarted) {
      payload.DeadlineDaysStarted = Number(val.DeadlineDaysStarted);
    } else {
      delete payload.DeadlineDaysStarted;
    }
    if (val.DeadlineDaysSubmitted) {
      payload.DeadlineDaysSubmitted = Number(val.DeadlineDaysSubmitted);
    } else {
      delete payload.DeadlineDaysSubmitted;
    }
    if (val.DeadlineDaysMoreInfo) {
      payload.DeadlineDaysMoreInfo = Number(val.DeadlineDaysMoreInfo);
    } else {
      delete payload.DeadlineDaysMoreInfo;
    }

    this.submitting = true;
    this.createProfile(payload);
  }

  createProfile(payload:any) {
    if(this.action === 'add'){
      this.profileService.createProfile(payload).subscribe({
        next: () => {
          this.utilService.openToastr('success', 'Approach profile created');
          this.dialogRef.close(true);
        },
        error: () => {
          this.utilService.openToastr('error', 'Failed to create approach profile');
          this.submitting = false;
        }
      });
    }else{
      this.profileService.updateProfile(payload).subscribe({
        next: () => {
          this.utilService.openToastr('success', 'Approach profile updated');
          this.dialogRef.close(true);
        },
        error: () => {
          this.utilService.openToastr('error', 'Failed to update approach profile');
          this.submitting = false;
        }
      });
    }
  }
}
